import React, { useState, useEffect } from "react";
import { FaFileUpload } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { AiOutlineEye } from "react-icons/ai";
import { useTranslation } from "react-i18next";
import { getMaxImageSize } from "@/utils/helpers";
import { AppConfig } from "@/utils/AppConfig";
import i18n from "@/i18n";

interface FileInputProps {
  onFileChange: (file: File | null, preview?: string | null) => void;
  isApiAttachment: boolean;
  disabled?: boolean;
}

const { MAX_IMAGE_SIZE, MAX_IMAGE_SIZE_MB_String } = getMaxImageSize(AppConfig.ImagesMaxSizeInMB.taxAttachment);

const FileInputImageAndPdf: React.FC<FileInputProps> = ({ onFileChange, isApiAttachment, disabled }) => {
  const { t } = useTranslation();
  const [, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const maxSize = i18n.language === "ar" ? MAX_IMAGE_SIZE_MB_String.ar : MAX_IMAGE_SIZE_MB_String.en;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];

    if (selectedFile) {
      const fileType = selectedFile.type;

      if (selectedFile.size > MAX_IMAGE_SIZE) {
        setError(t("fileInputAndPdf.fileTooLarge", { size: maxSize }));
        setFile(null);
        setPreview(null);
        setFileName(null);
        onFileChange(null, null);
        return;
      }

      if (fileType.startsWith("image/")) {
        setFile(selectedFile);
        setPreview(URL.createObjectURL(selectedFile));
        setFileName(selectedFile.name);
        setError(null);
        onFileChange(selectedFile, URL.createObjectURL(selectedFile));
      } else if (fileType === "application/pdf") {
        setFile(selectedFile);
        setFileName(selectedFile.name);
        setPreview(null);
        setError(null);
        onFileChange(selectedFile, null);
      } else {
        setError(t("fileInputAndPdf.invalidType"));
        setFile(null);
        setPreview(null);
        setFileName(null);
        onFileChange(null, null);
      }
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setPreview(null);
    setFileName(null);
    setError(null);
    onFileChange(null, null);

    const fileInput = document.getElementById("file-input") as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const closeModal = () => setIsModalOpen(false);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        isModalOpen &&
        (event.target as HTMLElement).classList.contains("modal-overlay")
      ) {
        closeModal();
      }
    };

    window.addEventListener("click", handleOutsideClick);
    return () => {
      window.removeEventListener("click", handleOutsideClick);
    };
  }, [isModalOpen]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex size-fit w-full rounded-md border border-gray-300">
        <input
          type="file"
          accept="image/png,image/jpeg,application/pdf"
          style={{ display: "none" }}
          onChange={handleFileChange}
          id="file-input"
          name="tax-record"
          disabled={disabled}
        />

        <button
          className={`min-w-[100px] rounded-s-md px-5 py-2 font-semibold ${
            disabled
              ? "cursor-not-allowed bg-gray-300 text-gray-500"
              : "cursor-pointer bg-sidebar text-skyblue"
          }`}
          onClick={() => document.getElementById("file-input")?.click()}
        >
          {isApiAttachment ? t("fileInputAndPdf.update") : t("fileInputAndPdf.upload")}
        </button>

        <div className="relative flex w-full flex-col items-center justify-center px-2 py-3 text-sm sm:min-w-[20rem] sm:text-base">
          {!preview && !fileName && !error && (
            <div
              className={`flex items-center justify-center gap-2 font-semibold ${
                disabled ? "text-gray-500" : "text-sidebar"
              }`}
            >
              <FaFileUpload />
              <span>
                {isApiAttachment ? t("fileInputAndPdf.chooseOther") : t("fileInputAndPdf.chooseLocation")}
              </span>
            </div>
          )}

          {error && (
            <div className="p-1 px-2 text-sm text-red-500">{error}</div>
          )}

          {fileName && (
            <p className="max-[400px]:max-w-[10rem] max-[400px]:truncate">
              {fileName}
            </p>
          )}

          {(preview || fileName) && (
            <button
              className="absolute end-0 top-0 p-1 text-lg text-red-500 hover:bg-black/10"
              onClick={handleRemoveFile}
            >
              <IoMdClose size={22} />
            </button>
          )}
        </div>
      </div>

      {preview && (
        <button
          className="rounded-full p-2 text-sidebar hover:bg-gray-200"
          onClick={() => setIsModalOpen(true)}
        >
          <AiOutlineEye size={24} />
        </button>
      )}

      {isModalOpen && (
        <div
          className="modal-overlay fixed inset-0 z-[9999] flex items-center justify-center bg-black/70"
          onClick={closeModal}
        >
          <div className="relative rounded-md bg-white p-4 shadow-lg">
            <button
              className="absolute right-2 top-2 rounded-full p-2 text-lg text-red-700 hover:bg-gray-200/70"
              onClick={closeModal}
            >
              <IoMdClose size={24} />
            </button>
            <img
              className="max-h-[85vh] w-auto max-w-[99dvw] object-contain"
              src={preview || undefined}
              alt="File Preview Modal"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FileInputImageAndPdf;
